import React, { useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuthStore } from "@/store/useAuthStore";
import { Mail, KeyRound } from "lucide-react";
import { Turnstile } from "@marsidev/react-turnstile";
import type { TurnstileInstance } from "@marsidev/react-turnstile";
import { supabase } from "@/services/supabase";
import {
  SUPABASE_PUBLISHABLE_KEY,
  SUPABASE_URL,
  TURNSTILE_SITE_KEY,
  DISPLAY_NAME_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
} from "@/config/publicConfig";

export default function Register() {
  const navigate = useNavigate();
  const { signInWithPassword } = useAuthStore();
  const turnstileRef = useRef<TurnstileInstance>(null);

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [turnstileToken, setTurnstileToken] = useState<string>("");

  // 表单状态
  const [loginMethod, setLoginMethod] = useState<"password" | "magiclink">(
    "password",
  );
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [role, setRole] = useState<"single" | "parent" | "child">("single");
  const [displayName, setDisplayName] = useState("");
  const [gender, setGender] = useState<"male" | "female" | "">("");

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!turnstileToken) {
      setErrorMsg("请先完成人机验证");
      return;
    }
    setLoading(true);
    setErrorMsg("");
    try {
      // 免密链接也需要携带人机验证 token，因此统一走 Edge Function
      const res = await fetch(`${SUPABASE_URL}/functions/v1/auth-turnstile`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({
          action: "signInWithOtp",
          email,
          turnstileToken,
        }),
      });

      const data = await res.json();
      if (!res.ok || data.error) {
        throw new Error(data.error || "发送失败");
      }

      alert("注册链接已发送到您的邮箱，请点击链接完成注册并登录！");
      // 免密注册链接：用户会从邮箱点回站内页面；若资料缺失，需要在站内引导完善
    } catch (err: any) {
      setErrorMsg(err.message || "发送失败，请重试");
      turnstileRef.current?.reset();
      setTurnstileToken("");
    } finally {
      setLoading(false);
    }
  };

  const handleNext = (e: React.FormEvent) => {
    e.preventDefault();
    if (step === 1 && !turnstileToken) {
      setErrorMsg("请先完成人机验证");
      return;
    }
    if (step === 1 && email && password.length >= PASSWORD_MIN_LENGTH) {
      setStep(2);
    } else if (step === 2 && role) {
      setStep(3);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!gender) {
      setErrorMsg("请选择性别");
      return;
    }

    setLoading(true);
    setErrorMsg("");

    try {
      const meta: Record<string, string> = {
        role,
        gender,
      };
      const dn = displayName.trim().slice(0, DISPLAY_NAME_MAX_LENGTH);
      if (dn) meta.display_name = dn;

      // Use edge function for signup too
      const res = await fetch(`${SUPABASE_URL}/functions/v1/auth-turnstile`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({
          action: "signUp",
          email,
          password,
          turnstileToken,
          options: {
            data: meta,
          },
        }),
      });

      const data = await res.json();
      if (!res.ok || data.error) {
        throw new Error(data.error || "注册失败");
      }

      // If session is returned, set it
      if (data.session) {
        await supabase.auth.setSession(data.session);
        navigate("/app");
      } else if (data.user && !data.user.confirmed_at) {
        alert("注册成功！请前往邮箱查收确认邮件完成激活后再登录。");
        navigate("/login");
      } else {
        alert("注册成功！正在为您登录...");
        await signInWithPassword(email, password);
        navigate("/app");
      }
    } catch (err: any) {
      setErrorMsg(err.message || "操作失败，请重试");
      // 出现验证异常或 Token 过期时，回退到第一步以便重新完成人机校验
      setTurnstileToken("");
      setStep(1);
      turnstileRef.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-pink-50 p-4">
      <img src="/favicon.png" alt="Logo" className="w-32 h-32 mb-4 rounded-3xl shadow-md" />
      <h1 className="text-3xl font-bold text-pink-600 mb-8">
        你妈帮你找对象 - 注册
      </h1>

      <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
        {/* Progress indicator */}
        <div className="flex justify-between mb-8">
          <div
            className={`h-2 w-full mx-1 rounded ${step >= 1 ? "bg-pink-500" : "bg-gray-200"}`}
          ></div>
          <div
            className={`h-2 w-full mx-1 rounded ${step >= 2 ? "bg-pink-500" : "bg-gray-200"}`}
          ></div>
          <div
            className={`h-2 w-full mx-1 rounded ${step >= 3 ? "bg-pink-500" : "bg-gray-200"}`}
          ></div>
        </div>

        <h2 className="text-2xl font-semibold mb-6 text-center text-gray-800">
          {step === 1 && "创建账号"}
          {step === 2 && "选择身份"}
          {step === 3 && "完善资料"}
        </h2>

        {errorMsg && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md text-sm text-center">
            {errorMsg}
          </div>
        )}

        {step === 1 && (
          <>
            {/* Toggle Login Method */}
            <div className="flex mb-6 p-1 bg-gray-100 rounded-lg">
              <button
                onClick={() => {
                  setLoginMethod("password");
                  setErrorMsg("");
                }}
                className={`flex-1 py-1.5 text-sm font-medium rounded-md flex items-center justify-center gap-2 transition-colors ${loginMethod === "password" ? "bg-white shadow-sm text-gray-800" : "text-gray-500 hover:text-gray-700"}`}
              >
                <KeyRound size={16} />
                密码注册
              </button>
              <button
                onClick={() => {
                  setLoginMethod("magiclink");
                  setErrorMsg("");
                }}
                className={`flex-1 py-1.5 text-sm font-medium rounded-md flex items-center justify-center gap-2 transition-colors ${loginMethod === "magiclink" ? "bg-white shadow-sm text-gray-800" : "text-gray-500 hover:text-gray-700"}`}
              >
                <Mail size={16} />
                免密链接
              </button>
            </div>

            {loginMethod === "password" ? (
              <form onSubmit={handleNext} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    邮箱地址
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full p-2 border rounded focus:ring-pink-500 focus:border-pink-500"
                    placeholder="请输入常用邮箱"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    密码
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full p-2 border rounded focus:ring-pink-500 focus:border-pink-500"
                    placeholder={`请输入密码 (至少 ${PASSWORD_MIN_LENGTH} 位)`}
                    minLength={PASSWORD_MIN_LENGTH}
                    required
                  />
                </div>

                <div className="flex justify-center mt-4 min-h-[65px]">
                  {TURNSTILE_SITE_KEY ? (
                    <Turnstile
                      ref={turnstileRef}
                      siteKey={TURNSTILE_SITE_KEY}
                      onSuccess={(token) => setTurnstileToken(token)}
                      onError={() => setErrorMsg("人机验证失败，请刷新重试")}
                      onExpire={() => setTurnstileToken("")}
                    />
                  ) : (
                    <div className="text-xs text-amber-700 bg-amber-50 p-2.5 rounded-lg border border-amber-200 text-center">
                      人机验证服务尚未配置 (缺少 VITE_TURNSTILE_SITE_KEY)
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={!turnstileToken}
                  className="w-full py-2 px-4 bg-pink-500 text-white rounded hover:bg-pink-600 disabled:opacity-50 transition-colors mt-6"
                >
                  下一步
                </button>
              </form>
            ) : (
              <form onSubmit={handleMagicLink} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    邮箱地址
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full p-2 border rounded-lg focus:ring-pink-500 focus:border-pink-500"
                    placeholder="请输入邮箱接收注册链接"
                    required
                  />
                </div>

                <div className="flex justify-center mt-4 min-h-[65px]">
                  {TURNSTILE_SITE_KEY ? (
                    <Turnstile
                      ref={turnstileRef}
                      siteKey={TURNSTILE_SITE_KEY}
                      onSuccess={(token) => setTurnstileToken(token)}
                      onError={() => setErrorMsg("人机验证失败，请刷新重试")}
                      onExpire={() => setTurnstileToken("")}
                    />
                  ) : (
                    <div className="text-xs text-amber-700 bg-amber-50 p-2.5 rounded-lg border border-amber-200 text-center">
                      人机验证服务尚未配置 (缺少 VITE_TURNSTILE_SITE_KEY)
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading || !turnstileToken}
                  className="w-full py-2 px-4 bg-pink-500 text-white rounded-lg hover:bg-pink-600 disabled:opacity-50 transition-colors"
                >
                  {loading ? "发送中..." : "发送免密注册链接"}
                </button>
              </form>
            )}
          </>
        )}

        {step === 2 && (
          <form onSubmit={handleNext} className="space-y-4">
            <div className="space-y-3">
              <label
                className={`block p-4 border rounded cursor-pointer ${role === "single" ? "border-pink-500 bg-pink-50" : "border-gray-200 hover:bg-gray-50"}`}
              >
                <input
                  type="radio"
                  name="role"
                  value="single"
                  className="hidden"
                  checked={role === "single"}
                  onChange={() => setRole("single")}
                />
                <div className="font-medium text-gray-800">我自己找对象</div>
                <div className="text-sm text-gray-500 mt-1">
                  我是单身，想为自己寻找另一半
                </div>
              </label>
              <label
                className={`block p-4 border rounded cursor-pointer ${role === "parent" ? "border-pink-500 bg-pink-50" : "border-gray-200 hover:bg-gray-50"}`}
              >
                <input
                  type="radio"
                  name="role"
                  value="parent"
                  className="hidden"
                  checked={role === "parent"}
                  onChange={() => setRole("parent")}
                />
                <div className="font-medium text-gray-800">我帮孩子找对象</div>
                <div className="text-sm text-gray-500 mt-1">
                  我是父母，想为子女寻找合适的伴侣
                </div>
              </label>
              <label
                className={`block p-4 border rounded cursor-pointer ${role === "child" ? "border-pink-500 bg-pink-50" : "border-gray-200 hover:bg-gray-50"}`}
              >
                <input
                  type="radio"
                  name="role"
                  value="child"
                  className="hidden"
                  checked={role === "child"}
                  onChange={() => setRole("child")}
                />
                <div className="font-medium text-gray-800">我帮父母找老伴</div>
                <div className="text-sm text-gray-500 mt-1">
                  我是子女，想为单身的父母寻找老伴
                </div>
              </label>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="w-1/3 py-2 px-4 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
              >
                返回
              </button>
              <button
                type="submit"
                className="w-2/3 py-2 px-4 bg-pink-500 text-white rounded hover:bg-pink-600 transition-colors"
              >
                下一步
              </button>
            </div>
          </form>
        )}

        {step === 3 && (
          <form onSubmit={handleRegister} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                您的昵称（可选，默认已由邮箱生成；最多 {DISPLAY_NAME_MAX_LENGTH}{" "}
                字）
              </label>
              <input
                type="text"
                maxLength={DISPLAY_NAME_MAX_LENGTH}
                value={displayName}
                onChange={(e) =>
                  setDisplayName(
                    e.target.value.slice(0, DISPLAY_NAME_MAX_LENGTH),
                  )
                }
                className="w-full p-2 border rounded focus:ring-pink-500 focus:border-pink-500"
                placeholder="留空则使用系统默认昵称"
              />
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                相亲者的性别（如果是帮人找，请选对方的性别）
              </label>
              <div className="flex gap-4">
                <label
                  className={`flex-1 p-3 text-center border rounded cursor-pointer ${gender === "male" ? "border-pink-500 bg-pink-50 text-pink-700" : "border-gray-200 hover:bg-gray-50 text-gray-600"}`}
                >
                  <input
                    type="radio"
                    name="gender"
                    value="male"
                    className="hidden"
                    checked={gender === "male"}
                    onChange={() => setGender("male")}
                  />
                  男
                </label>
                <label
                  className={`flex-1 p-3 text-center border rounded cursor-pointer ${gender === "female" ? "border-pink-500 bg-pink-50 text-pink-700" : "border-gray-200 hover:bg-gray-50 text-gray-600"}`}
                >
                  <input
                    type="radio"
                    name="gender"
                    value="female"
                    className="hidden"
                    checked={gender === "female"}
                    onChange={() => setGender("female")}
                  />
                  女
                </label>
              </div>
            </div>

            <div className="flex gap-3 mt-8">
              <button
                type="button"
                onClick={() => setStep(2)}
                className="w-1/3 py-2 px-4 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
              >
                返回
              </button>
              <button
                type="submit"
                disabled={loading || !gender}
                className="w-2/3 py-2 px-4 bg-pink-500 text-white rounded hover:bg-pink-600 disabled:opacity-50 transition-colors"
              >
                {loading ? "注册中..." : "完成注册"}
              </button>
            </div>
          </form>
        )}

        <div className="mt-8">
          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">
                已经有账号了？
              </span>
            </div>
          </div>
          <Link
            to="/login"
            className="w-full flex items-center justify-center py-2 px-4 border-2 border-pink-500 text-pink-600 font-medium rounded-lg hover:bg-pink-50 transition-colors"
          >
            直接去登录
          </Link>
        </div>
      </div>
    </div>
  );
}
